import { Settings } from 'luxon';
import { type JSX } from 'react';
import { createRoot } from 'react-dom/client';
import ArtviseChat from './components/artvise-chat/artvise-chat';
import LogService from './react/services/log.service';

// React components
import BlogList from './react/components/BlogList';
import LostAndFound from './react/components/LostAndFound';
import CookieConsent from './react/components/CookieConsent';
import TicketPrices from './react/components/TicketPrices';
import TrafficInformationOverview from './react/components/TrafficInformation/TrafficInformationOverview';
import TrafficInformationSearch from './react/components/TrafficInformation/TrafficInformationSearch';
import DepartureBoardLinkGenerator from './react/components/DepartureBoardLinkGenerator';
import PressPage from './react/components/Press';
import StopAreaSearchDetails from './react/components/StopAreas/StopAreaSearchDetails';
import StopAreaSearch from './react/components/StopAreas/StopAreaSearch';
import Timetables from './react/components/Timetables';
import TicketInspectionSurchargeForm from './react/components/TicketInspectionSurchargeForm';
import SiteSearchDesktop from './react/components/SiteSearchDesktop';
import SearchPage from './react/components/Search';
import Reclamation from './react/components/Reclamation';
import SingleTickets from './react/components/SingleTickets';
import AreaTicketDetails from './react/components/AreaTickets/AreaTicketDetails';
import SiteSearchMobile from './react/components/SiteSearchMobile';
import NextDeparture from './react/components/NextDeparture';
import AreaTicketSearch from './react/components/AreaTickets/AreaTicketSearch';
import QuestionsAndOpinions from './react/components/QuestionsAndOpinions';
import CertificateCardForm from './react/components/CertificateCardForm';
import TapAndGoReceipts from './react/components/TapAndGoReceipts';
import TravelPlanner from './react/components/TravelPlanner';
import RetailerMap from './react/components/RetailerMap';
import DelayCompensation from './react/components/DelayCompensation/DelayCompensationVasttrafik';
import DelayCompensationOresundstag from './react/components/DelayCompensation/DelayCompensationOresundstag';
import { TranslationService } from './react/services/translation.service';
import { ConfigurationService } from './react/services/configuration.service';
import MicromobilitySearch from './react/components/Labs/MicromobilitySearch';
import Scoreboard from './react/components/PaEgnaBen/Scoreboard';
import PriceCalculatorPage from './react/components/PriceCalculatorPage';
import TravelAssistanceCardForm from './react/components/TravelAssistanceCardForm';

import createPaEgnaBenAdminstratorRouterProvider from './react/components/PaEgnaBen/Administrator/administrator.router-provider-factory';
import createPaEgnaBenTeacherRouterProvider from './react/components/PaEgnaBen/Teacher/teacher.router-provider-factory';
import createPaEgnaBenMunicipalityAdministratorRouterProvider from './react/components/PaEgnaBen/MunicipalityAdministrator/municipalityAdministrator.router-provider-factory';
import createPaEgnaBenParticipationOverviewRouterProvider from './react/components/PaEgnaBen/ParticipationOverview/ParticipationOverview.router-provider-factory';

import LocalServiceMap from './react/components/LocalServiceMap';
import MunicipalitySelectorModal from './react/components/PaEgnaBen/MunicipalitySelectorModal';

LogService.init();

new ArtviseChat();

TranslationService.init();

Settings.defaultZone = 'Europe/Stockholm';

const { lang } = document.getElementsByTagName('html')[0];
if (lang) {
  Settings.defaultLocale = lang;
}

function render(id: string, node: React.ReactNode) {
  if (document.getElementById(id) != null) {
    createRoot(document.getElementById(id) as Element).render(node);
  }
}

function renderTicketPricesBlocks() {
  const elements = document.getElementsByClassName('ticket-prices-app');
  for (let i = 0; i < elements.length; i += 1) {
    const element = elements[i] as HTMLElement;
    if (element.dataset.guid) {
      createRoot(element).render(<TicketPrices guid={element.dataset.guid} />);
    }
  }
}

function renderLocalServiceMapBlocks() {
  const elements = document.getElementsByClassName('local-service-map-block');
  for (let i = 0; i < elements.length; i += 1) {
    const element = elements[i] as HTMLElement;
    if (element.dataset.gid) {
      createRoot(element).render(<LocalServiceMap gid={element.dataset.gid} />);
    }
  }
}

function renderRouter(id: string, createRouterProviderFunc: () => JSX.Element) {
  if (document.getElementById(id) != null) {
    createRoot(document.getElementById(id) as Element).render(createRouterProviderFunc());
  }
}

render('blog-list', <BlogList />);
render('lost-and-found', <LostAndFound />);
render('traffic-information-overview', <TrafficInformationOverview />);
render('cookie-consent', <CookieConsent />);
render('traffic-information-search', <TrafficInformationSearch />);
render('departureboardlinkgeneratorpage', <DepartureBoardLinkGenerator />);
render('press-page', <PressPage />);
render('stopareasearchdetailspage', <StopAreaSearchDetails />);
render('timetables', <Timetables />);
render('stopareasearchpage', <StopAreaSearch />);
render('ticket-inspection-surcharge-appeal-form', <TicketInspectionSurchargeForm />);
render('search-page', <SearchPage />);
render('area-ticket-details-page', <AreaTicketDetails />);
render('dayticketspage', <AreaTicketSearch productType="dayticket" />);
render('periodticketspage', <AreaTicketSearch productType="periodticket" />);
render('reclamationform', <Reclamation />);
render('single-ticket-search', <SingleTickets />);
renderTicketPricesBlocks();
render('site-search-desktop-placeholder', <SiteSearchDesktop />);
render('questionsandopinions', <QuestionsAndOpinions />);
render('site-search-form-mobile', <SiteSearchMobile />);
render('next-departure', <NextDeparture />);
render('tap-and-go-receipts-page', <TapAndGoReceipts />);
render('certificate-card-form', <CertificateCardForm />);
render('travel-planner-startpage', <TravelPlanner initalShowSearchDetails={false} headingText={TranslationService.get('travelplanner.heading')} />);
render('travel-planner-page', <TravelPlanner initalShowSearchDetails={true} headerHtml={ConfigurationService.get('travelplannerpage.header')} />);
render('retailer-map', <RetailerMap />);
render('delaycompensationform', <DelayCompensation />);
render('delaycompensationoresundstagform', <DelayCompensationOresundstag />);
render('micromobilitysearchpage', <MicromobilitySearch />);
render('pa-egna-ben-scoreboard', <Scoreboard />);
render('municipality-selector-modal', <MunicipalitySelectorModal />);

render('price-calculator-page', <PriceCalculatorPage />);
render('travel-assistance-card-form', <TravelAssistanceCardForm />);
renderLocalServiceMapBlocks();
renderRouter('pa-egna-ben-admin', createPaEgnaBenAdminstratorRouterProvider);
renderRouter('pa-egna-ben-teacher', createPaEgnaBenTeacherRouterProvider);
renderRouter('pa-egna-ben-municipality-administrator', createPaEgnaBenMunicipalityAdministratorRouterProvider);
renderRouter('pa-egna-ben-participation-overview', createPaEgnaBenParticipationOverviewRouterProvider);
